import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@cbhq/cds-web/buttons';
import { InputIcon, TextInput } from '@cbhq/cds-web/controls';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Avatar } from '@cbhq/cds-web/media';
import { TextBody, TextHeadline, TextTitle1 } from '@cbhq/cds-web/typography';

import { AnalyticEvent } from ':fat/analytics/events';
import { logGenericEvent, logRender } from ':fat/analytics/logActions';
import { FundSettlementFAQ } from ':fat/components/FAQ/FundsSettlementFAQ';
import { TwoColumnLayout } from ':fat/components/PageLayout/TwoColumnLayout';
import { ALL_NETWORKS, CURRENCY_DATA, Network } from ':fat/core/networks';
import { formatCrypto, formatFiat } from ':fat/helpers/formatting';
import { getCurrencyIcon } from ':fat/helpers/getCurrencyIcon';
import { isAddressValid } from ':fat/helpers/isAddressValid';
import { useBalancesForFingerprint } from ':fat/hooks/useBalances';
import { constructTransactions } from ':fat/messaging/entities/blockchainWorker/actions';
import { clearAllData } from ':fat/messaging/entities/databaseWorker/actions';
import { messages } from ':fat/pages/EnterWalletAddresses/EnterWalletAddressesPageMessages';
import { AppRoutes } from ':fat/routing/routes';
import { useTransactions } from ':fat/TransactionContext';

export function EnterWalletAddressesPage() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { fingerprint } = useParams();
  const { setTransactions } = useTransactions();
  const { balances, fiatBalances } = useBalancesForFingerprint(fingerprint);
  const [constructing, setConstructing] = useState(false);
  const [addresses, setAddresses] = useState<Record<Network, string>>(
    ALL_NETWORKS.reduce(
      (initial, network) => ({
        ...initial,
        [network]: localStorage.getItem(`${network}-address`) ?? '',
      }),
      {} as Record<Network, string>,
    ),
  );

  const allAddressesEntered =
    Object.keys(balances).length &&
    Object.keys(balances).every(
      (network) =>
        (balances[network as Network] ?? 0n) === 0n ||
        (!!addresses[network as Network] &&
          isAddressValid(addresses[network as Network], network as Network)),
    );

  useEffect(() => {
    logRender(AnalyticEvent.EnterWalletAddressesPageRendered);
  }, []);

  const handleUpdateText = useCallback(
    (network: Network, event: React.ChangeEvent<HTMLInputElement>) => {
      localStorage.setItem(`${network}-address`, event.target.value);
      setAddresses((prevState) => ({ ...prevState, [network]: event.target.value }));
    },
    [],
  );

  const handleBackPress = useCallback(() => {
    void clearAllData().then(() => {
      navigate(AppRoutes.SECURITY_FRAME_LOADER);
    });
  }, [navigate]);

  const handleContinuePress = useCallback(() => {
    logGenericEvent(AnalyticEvent.SubmitWalletAddressesClicked);

    async function constructAndContinue() {
      setConstructing(true);
      const fp = parseInt(fingerprint ?? '');
      const txs = await constructTransactions(fp, addresses);
      setTransactions(txs);
      setTimeout(() => {
        navigate(AppRoutes.REVIEW.replace(':fingerprint', fp.toString()));
      }, 500);
    }

    if (allAddressesEntered) {
      void constructAndContinue();
    }
  }, [allAddressesEntered, addresses, setTransactions, fingerprint, navigate]);

  return (
    <TwoColumnLayout
      primaryContent={
        <VStack gap={2} width="100%">
          <VStack gap={2}>
            <TextTitle1 as="h1">{formatMessage(messages.title)}</TextTitle1>
            <TextBody color="foregroundMuted" as="p">
              {formatMessage(messages.description)}
            </TextBody>
          </VStack>

          {ALL_NETWORKS.map((network) =>
            (balances[network] ?? 0n) === 0n ? null : (
              <VStack key={network}>
                <HStack gap={1} spacingVertical={1}>
                  <Avatar alt="" src={getCurrencyIcon(network)} size="xl" />
                  <VStack>
                    <TextHeadline as="p">{CURRENCY_DATA[network].name}</TextHeadline>
                    <TextBody color="foregroundMuted" as="p">
                      {`${formatFiat(fiatBalances[network] ?? 0)} · ${formatCrypto(
                        network,
                        balances[network] ?? 0n,
                      )}`}
                    </TextBody>
                  </VStack>
                </HStack>

                <Box spacingBottom={2} height={100}>
                  <TextInput
                    value={addresses[network]}
                    label={formatMessage(messages.inputLabel, {
                      asset: CURRENCY_DATA[network].name,
                    })}
                    placeholder={formatMessage(messages.inputPlaceholder, {
                      asset: CURRENCY_DATA[network].name,
                    })}
                    variant={
                      addresses[network] && !isAddressValid(addresses[network], network)
                        ? 'negative'
                        : 'foreground'
                    }
                    helperText={
                      addresses[network] &&
                      !isAddressValid(addresses[network], network) &&
                      formatMessage(messages.inputHelperText, {
                        asset: CURRENCY_DATA[network].name,
                      })
                    }
                    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
                    onChange={(event) => handleUpdateText(network, event)}
                    end={
                      isAddressValid(addresses[network], network) ? (
                        <InputIcon name="checkmark" />
                      ) : undefined
                    }
                  />
                </Box>
              </VStack>
            ),
          )}

          <HStack gap={1} spacingTop={2} justifyContent="flex-end">
            <Button variant="secondary" onPress={handleBackPress}>
              {formatMessage(messages.back)}
            </Button>
            <Button
              endIcon="forwardArrow"
              variant="primary"
              onPress={handleContinuePress}
              disabled={!allAddressesEntered || constructing}
              loading={constructing}
            >
              {formatMessage(constructing ? messages.loading : messages.continue)}
            </Button>
          </HStack>
        </VStack>
      }
      secondaryContent={<FundSettlementFAQ />}
    />
  );
}
